/**
 * LiveKit meeting room API client — room lifecycle, token generation,
 * guest links, and email invitations.
 */

import { apiClient } from "./client";
import type {
  MeetingRecordStart,
  MeetingRoom,
  LiveKitTokenResponse,
  GuestLink,
  GuestJoinInfo,
  MeetingInvitation,
  MeetingControlChange,
  FelixMeetingContext,
  MeetingHistoryEntry,
  MeetingTranscriptSearch,
  TranscriptSegment,
  MeetingIntelligenceSnapshot,
  MeetingMediaStatsBatch,
} from "./types";

export interface WaitingGuest {
  guest_link_id: string;
  arrival_id: string;
  display_name: string | null;
  waiting_since: string;
  status: string;
}

/** Metadata for a file/image shared into a meeting's chat. Mirrors the
 *  meeting_attachment_service.store() RETURNING row (no bytes). */
export interface MeetingAttachment {
  id: string;
  file_name: string;
  content_type: string;
  kind: "image" | "document" | string;
  size_bytes: number;
  created_at?: string;
}

/** Carries the HTTP status so the caller can tell a 403 (guests are refused
 *  file sharing in v1) from a validation 4xx or a transient failure. */
export class MeetingAttachmentError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "MeetingAttachmentError";
    this.status = status;
  }
}

/** A stable id for THIS browser's arrival at a meeting.
 *
 * A guest link is reusable and shared — the calendar producer issues one link
 * with no email for every invitee — so the waiting room is keyed per arrival,
 * not per link. This id must be the SAME across the initial join, the
 * 90-minute token refresh, a reconnect, and the 401 retry: the server treats a
 * known arrival that has been admitted as admitted and lets it straight back
 * through, and treats an unknown one as a fresh knock. Send a new id from a
 * guest already in the room and they are put back in the lobby mid-meeting.
 *
 * sessionStorage, not localStorage: a second tab is genuinely a second
 * arrival, and the wait should not outlive the browser session.
 */
export function guestArrivalId(): string {
  const KEY = "ftl_guest_arrival_id";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = (globalThis.crypto?.randomUUID?.() ?? `a-${Date.now()}-${Math.random()}`)
      .replace(/-/g, "")
      .slice(0, 32);
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

/** Methods the table accepts. Only EXPLICIT_UI, VERBAL_ATTESTED and
 *  PRIOR_WRITTEN actually CONSENT — IMPLIED_NOTICE is recorded but does not.
 *  "We told them we were recording" is not "they agreed", and in a two-party
 *  jurisdiction only the second one is consent. */
export type RecordingConsentMethod =
  | "EXPLICIT_UI"
  | "VERBAL_ATTESTED"
  | "PRIOR_WRITTEN"
  | "IMPLIED_NOTICE";

export interface RecordingConsentSubject {
  subject: string;
  user_id: string | null;
  participant_email: string | null;
  participant_label: string | null;
  method: RecordingConsentMethod;
  revocation_event: boolean;
  consent_version: string | null;
  recorded_at: string;
  /** Resolved exactly as the gate resolves it, so this cannot disagree with
   *  whether recording is actually permitted. */
  consents: boolean;
  /** Agreed to an earlier notice that a later MATERIAL change superseded, so
   *  this person is asked again (row 0d53b02b, founder answer 5). Optional:
   *  an older server does not send it. */
  notice_outdated?: boolean;
}

import type { RecordingDisclosure } from "@/components/meeting/recordingConsentNotice";
import { GUEST_ACCEPTED_NOTICE_KEY } from "@/components/meeting/guestTranscriptionNotice";
import { GUEST_ACCEPTED_RECORDING_KEY } from "@/components/meeting/recordingConsentToJoin";
import { storedIsInvitee } from "@/components/meeting/guestInvitee";

export interface RecordingConsentState {
  policy: "NONE" | "HOST_ONLY" | "ALL_PARTY";
  subjects: RecordingConsentSubject[];
  /** The facts the consent notice states, served rather than written into the
   *  UI. Retention and capture mode are configurable per deployment, and a
   *  notice claiming "90 days" while this deployment is set to 30 is a false
   *  statement made to someone with no way to check it. Optional because an
   *  older server will not send it — the panel renders no notice rather than
   *  an invented one. */
  disclosure?: RecordingDisclosure;
}


export const meetingRoomApi = {
  // ── Room lifecycle ────────────────────────────────────────────────

  createRoom: (meetingId: string, opts?: { max_participants?: number; recording_enabled?: boolean }) =>
    apiClient.post<MeetingRoom>(`/meetings/${meetingId}/room`, {
      max_participants: opts?.max_participants ?? 50,
      recording_enabled: opts?.recording_enabled ?? false,
    }),

  // ── Token generation ──────────────────────────────────────────────

  getToken: (meetingId: string) =>
    apiClient.post<LiveKitTokenResponse>(`/meetings/${meetingId}/token`, {}),

  /**
   * Re-mint a guest's LiveKit token mid-meeting WITHOUT consuming the invite
   * link. Authenticates with the already-issued guest JWT (attached as Bearer by
   * apiClient), so a WebRTC reconnect no longer burns a single-use link
   * (guest-link C). Deliberately does NOT return a new guest_jwt — the JWT
   * expiring is the correct end of the guest session, at which point the caller
   * falls back to link re-auth (multi-use links only).
   */
  guestReconnectToken: (meetingId: string) =>
    apiClient.post<{
      livekit_token: string;
      room_name: string;
      livekit_url: string;
      meeting_id: string;
      permission_level: string;
      expires_in: number;
    }>(`/meetings/${meetingId}/guest-token`, {}),

  // ── Guest link management ─────────────────────────────────────────

  createGuestLink: (meetingId: string, opts: {
    email?: string;
    display_name?: string;
    permission_level?: string;
    waiting_room_required?: boolean;
    /** One-shot link: consumed on first join, rejected thereafter. */
    single_use?: boolean;
  }) =>
    apiClient.post<GuestLink>(`/meetings/${meetingId}/guest-link`, opts),

  listGuestLinks: (meetingId: string) =>
    apiClient.get<{ items: GuestLink[]; total: number }>(`/meetings/${meetingId}/guest-links`),

  revokeGuestLink: (meetingId: string, linkId: string) =>
    apiClient.delete(`/meetings/${meetingId}/guest-link/${linkId}`),

  // ── Recording (LiveKit egress) ────────────────────────────────────

  startRecording: (meetingId: string) =>
    apiClient.post<{ status: string }>(`/meetings/${meetingId}/recording/start`, {}),

  stopRecording: (meetingId: string) =>
    apiClient.post<{ status: string }>(`/meetings/${meetingId}/recording/stop`, {}),

  /** Ask Felix to wrap the meeting up. Does NOT end the meeting — "End for
   *  all" is the separate, destructive action. Row f54dd39e. */
  requestWrapUp: (meetingId: string) =>
    apiClient.post<{ requested: boolean; requested_at: string | null; requested_by: string | null }>(
      `/meetings/${meetingId}/wrap-up`, {}),

  getWrapUpRequest: (meetingId: string) =>
    apiClient.get<{ requested: boolean; requested_at: string | null; requested_by: string | null }>(
      `/meetings/${meetingId}/wrap-up`),

  /** When the meeting's record (transcript and minutes) starts — founder
   *  6467901243. Nothing said before it is kept. */
  getRecordStart: (meetingId: string) =>
    apiClient.get<MeetingRecordStart>(`/meetings/${meetingId}/record`),

  /** Host-only: "start_now" starts the record at once; "not_yet" holds it past
   *  the scheduled start until the host starts it (founder 6468787291). */
  setRecordStart: (meetingId: string, action: "start_now" | "not_yet") =>
    apiClient.post<MeetingRecordStart>(`/meetings/${meetingId}/record`, { action }),

  listRecordings: (meetingId: string) =>
    apiClient.get<{ items: Array<{ id: string; status: string }> }>(
      `/meetings/${meetingId}/recordings`,
    ),

  // ── Recording consent (migration 582) ─────────────────────────────
  //
  // The gate has guarded the recording path since it shipped, failing closed to
  // ALL_PARTY. The route that WRITES a consent row shipped too. Nothing called
  // it: measured 2026-08-28, meeting_recording_consents held 0 rows and the
  // resolved policy is ALL_PARTY, so every start-recording attempt was refused.
  // As the route's own docstring puts it, a gate nobody can pass is not a
  // policy, it is an outage with a rationale.

  getRecordingConsent: (meetingId: string) =>
    apiClient.get<RecordingConsentState>(`/meetings/${meetingId}/recording-consent`),

  /** Give or withdraw consent. Append-only: withdrawing posts a NEW event with
   *  `revocation_event: true` rather than editing, because consent is a
   *  sequence and editing would destroy the fact that it was once given. */
  recordRecordingConsent: (
    meetingId: string,
    body: {
      method: RecordingConsentMethod;
      /** WHICH consent text the person agreed to. REQUIRED by the route
       *  (`Field(..., min_length=1)`), and omitting it returns 422 — the panel
       *  shipped without it and could not record a single consent. A consent
       *  record that cannot say what was consented TO is not evidence of
       *  anything, which is why the field has no default server-side. */
      consent_version: string;
      revocation_event?: boolean;
    },
  ) =>
    apiClient.post<{ id: string }>(
      `/meetings/${meetingId}/recording-consent`, body,
    ),

  /** The member read of a meeting (org-scoped to the caller's active org).
   *  The guest join page uses it to tell a signed-in MEMBER of the meeting's
   *  org, who goes to the member lobby, from a signed-in outsider, who keeps
   *  the guest path (row 370236ca). A 404 says nothing about whether the
   *  meeting exists. */
  getMeetingAsMember: (meetingId: string) =>
    apiClient.get<{ id: string }>(`/meetings/${meetingId}`),

  // ── Guest join (public, no auth) ──────────────────────────────────

  validateGuestToken: (token: string) =>
    apiClient.get<GuestJoinInfo>(`/meetings/join/${token}`),

  /** Exchange a guest link token for a guest JWT + LiveKit token.
   *
   * On a meeting that screens its guests this returns `{status: "waiting"}`
   * and NO credentials at all — see `guestArrivalId` for why the arrival id
   * must be sent on every call, including the mid-meeting refreshes.
   */
  guestAuth: (
    token: string,
    displayName: string,
    acceptedNoticeVersion?: string,
    acceptedRecordingNoticeVersion?: string,
    isInvitee?: boolean,
  ) =>
    apiClient.post<{
      // present only while the host has not admitted this arrival; every
      // credential field below is absent in that case
      status?: "waiting";
      arrival_id?: string;
      guest_jwt: string;
      livekit_token: string;
      room_name: string;
      livekit_url: string;
      meeting_id: string;
      meeting_title: string;
      permission_level: string;
    }>("/meetings/guest-auth", {
      token,
      display_name: displayName,
      arrival_id: guestArrivalId(),
      // Row cd7ca6c9: the transcription notice version the guest accepted by
      // pressing Join. The join screen passes it; the mid-meeting re-auths
      // (90-minute refresh, reconnect fallback) carry the one stored then.
      // Without it the server refuses: no acceptance, no join.
      accepted_notice_version:
        acceptedNoticeVersion ?? sessionStorage.getItem(GUEST_ACCEPTED_NOTICE_KEY),
      // Row 718e501d PR 2: the recording notice version the guest agreed to,
      // once the server asked (409 recording_consent_required while a
      // recording runs). Kept for the re-auths like the one above.
      accepted_recording_notice_version:
        acceptedRecordingNoticeVersion ?? sessionStorage.getItem(GUEST_ACCEPTED_RECORDING_KEY),
      // Founder 6443495747: on an emailed invite, whether this is the person it was sent to. The
      // re-auths carry the answer given at the join screen, so someone the invite was forwarded to is
      // never re-admitted as the invitee by the 90-minute refresh.
      is_invitee: isInvitee ?? storedIsInvitee(),
    }),

  // ── Waiting room (host side) ──────────────────────────────────────
  //
  // These endpoints existed on the server for months with no caller at all,
  // which is why switching the waiting room on screened nobody: there was no
  // way for a host to see or admit anyone.

  /** Arrivals still awaiting a decision, oldest first. */
  listWaitingRoom: (meetingId: string) =>
    apiClient.get<{ items: WaitingGuest[] }>(
      `/meetings/${meetingId}/waiting-room`,
    ),

  /** Let one arrival in. Records a decision; the guest's own poll collects
   *  their token, so nothing here returns a credential. */
  admitGuest: (meetingId: string, linkId: string, arrivalId: string) =>
    apiClient.post<{ status: string }>(
      `/meetings/${meetingId}/waiting-room/${linkId}/admit?arrival_id=${encodeURIComponent(arrivalId)}`,
      {},
    ),

  /** Decline one arrival. Reversible — it does NOT revoke their invite. */
  rejectGuest: (meetingId: string, linkId: string, arrivalId: string, reason?: string) =>
    apiClient.post<{ status: string }>(
      `/meetings/${meetingId}/waiting-room/${linkId}/reject?arrival_id=${encodeURIComponent(arrivalId)}`,
      { reason },
    ),

  // ── Email invitations ─────────────────────────────────────────────

  sendInvitations: (meetingId: string, recipientEmails: string[]) =>
    apiClient.post<{ invitations: MeetingInvitation[]; total: number }>(
      `/meetings/${meetingId}/invite`,
      { recipient_emails: recipientEmails },
    ),

  // ── Transcript polling ────────────────────────────────────────────

  getTranscript: (meetingId: string, since?: number | null) =>
    apiClient.get<{ segments: TranscriptSegment[] }>(
      `/meetings/${meetingId}/transcript`,
      since != null ? { since } : undefined,
    ),

  /** The meeting chat stored so far (row efef4c4d): what a late joiner or a
   *  reload shows before the live messages. Guest-scoped by the route. */
  getChatHistory: (meetingId: string) =>
    apiClient.get<{ segments: TranscriptSegment[] }>(
      `/meetings/${meetingId}/transcript`,
      { chat_only: true, limit: 5000 },
    ),

  // ── Real-time meeting intelligence (Wave 5) ───────────────────────

  getIntelligence: (meetingId: string) =>
    apiClient.get<MeetingIntelligenceSnapshot>(`/meetings/${meetingId}/intelligence`),

  // ── P7 measure (row 80eb7318): in-room WebRTC stats batch, every ~10 s ──

  postMediaStats: (meetingId: string, batch: MeetingMediaStatsBatch) =>
    apiClient.post<{ recorded: boolean }>(`/meetings/${meetingId}/media-stats`, batch),

  // ── Phase 3: Mid-meeting Felix controls ───────────────────────────

  changeFelixControls: (meetingId: string, changeType: string, newValue: unknown, reason?: string) =>
    apiClient.post<MeetingControlChange>(`/meetings/${meetingId}/felix-controls`, {
      change_type: changeType,
      new_value: newValue,
      reason,
    }),

  listFelixControlChanges: (meetingId: string) =>
    apiClient.get<{ items: MeetingControlChange[]; total: number }>(
      `/meetings/${meetingId}/felix-controls`,
    ),

  getFelixContext: (meetingId: string) =>
    apiClient.get<FelixMeetingContext>(`/meetings/${meetingId}/felix-context`),

  // ── Phase 3: Meeting history ──────────────────────────────────────

  getMeetingHistory: (projectId: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<{ items: MeetingHistoryEntry[]; total: number }>(
      `/meetings/project/${projectId}/history`,
      params,
    ),

  searchTranscripts: (projectId: string, query: string) =>
    apiClient.get<{ items: MeetingTranscriptSearch[]; total: number }>(
      `/meetings/project/${projectId}/transcript-search`,
      { q: query },
    ),

  // ── In-meeting chat attachments (image / file share) ──────────────
  // Mirrors iteratorApi's upload: a multipart POST needs the browser-set
  // boundary, which the shared apiClient can't send (it forces JSON), so these
  // two use raw fetch with the same auth header. Backend endpoints:
  //   POST /meetings/{id}/attachments        -> MeetingAttachment (org member;
  //                                             guests get 403 by design)
  //   GET  /meetings/{id}/attachments/{aid}  -> the bytes (nosniff), for render
  uploadAttachment: (meetingId: string, file: File): Promise<MeetingAttachment> => {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("ftl_access_token");
    const baseUrl: string =
      (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ??
      "/api/v1";
    return fetch(`${baseUrl}/meetings/${meetingId}/attachments`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (res) => {
      if (!res.ok) {
        throw new MeetingAttachmentError(
          res.status,
          (await res.text().catch(() => "")) || `Upload failed (${res.status})`,
        );
      }
      return res.json() as Promise<MeetingAttachment>;
    });
  },

  fetchAttachmentBlob: (meetingId: string, attachmentId: string): Promise<Blob> => {
    const token = localStorage.getItem("ftl_access_token");
    const baseUrl: string =
      (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ??
      "/api/v1";
    return fetch(`${baseUrl}/meetings/${meetingId}/attachments/${attachmentId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((res) => {
      if (!res.ok) {
        throw new MeetingAttachmentError(
          res.status,
          `Failed to load attachment (${res.status})`,
        );
      }
      return res.blob();
    });
  },
};
